import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { detectPlatform } from "@ect/core";
import { Button } from "@ect/ui";
import { useSession } from "../app/session";
import { disablePush, enablePush, pushState, type PushState } from "../app/push";
import { openNotificationTarget } from "../app/notifications-os";

/** Notifications: enable on this device + per-type switches (PRD M13-01). */
export function NotificationSettings() {
  const { t } = useTranslation();
  const { api, session } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const platform = detectPlatform();
  const [state, setState] = useState<PushState>("prompt");
  const [types, setTypes] = useState<string[]>([]);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [devices, setDevices] = useState<{ device_id: string; platform: string | null }[]>([]);
  const isAdmin = !!session?.user.roles.some((r) => r.role === "group_admin" || r.role === "entity_admin");
  const reload = useCallback(async () => {
    setState(await pushState());
    const [cfg, p, d] = await Promise.all([api.json<{ types: string[] }>("/api/notifications/push-config"), api.json<Record<string, boolean>>("/api/notifications/preferences"), api.json<{ device_id: string; platform: string | null }[]>("/api/notifications/devices")]);
    setTypes((cfg as { types: string[] }).types ?? []); setPrefs((p as Record<string, boolean>) ?? {}); setDevices((d as { device_id: string; platform: string | null }[]) ?? []);
  }, [api]);
  useEffect(() => { void reload().catch(() => setError(t("notif.error"))); }, [reload, t]);
  const changeDevice = async (enable: boolean) => {
    setBusy(true); setError("");
    try {
      if (enable) await enablePush(api, (d) => openNotificationTarget(d, isAdmin));
      else await disablePush(api);
      await reload();
    } catch { setError(t("notif.error")); }
    finally { setBusy(false); }
  };
  const toggle = async (type: string) => {
    const next = { ...prefs, [type]: prefs[type] === false };
    setBusy(true); setError("");
    try {
      await api.json<Record<string, boolean>>("/api/notifications/preferences", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(next) });
      setPrefs(next);
    } catch { setError(t("notif.error")); }
    finally { setBusy(false); }
  };
  const thisDevice = devices.some((d) => d.device_id === api.deviceId);
  return (
    <div className="grid gap-4">
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      <div className="rounded-md border border-line bg-surface p-4">
        <div className="font-semibold">{t("notif.device")}</div>
        <p className="mt-1 text-sm text-ink-2">{platform === "tauri" ? t("notif.desktopInfo") : state === "unsupported" ? t("notif.unsupported") : state === "denied" ? t("notif.denied") : thisDevice ? t("notif.enabled") : t("notif.disabled")}</p>
        {platform !== "tauri" && state !== "unsupported" && (
          <div className="mt-3 flex gap-2">
            {!thisDevice && state !== "denied" && <Button size="sm" loading={busy} onClick={() => void changeDevice(true)}>{t("notif.enable")}</Button>}
            {thisDevice && <Button size="sm" loading={busy} variant="secondary" onClick={() => void changeDevice(false)}>{t("notif.disable")}</Button>}
          </div>
        )}
        {devices.length > 0 && <p className="mt-2 text-xs text-ink-3">{t("notif.devices", { count: devices.length })}</p>}
      </div>
      <div className="rounded-md border border-line bg-surface p-4">
        <div className="font-semibold">{t("notif.types")}</div>
        <ul className="mt-2 divide-y divide-line">
          {types.map((k) => (
            <li key={k} className="flex items-center justify-between py-2.5">
              <span className="text-sm">{t(`notif.type.${k}`, { defaultValue: k })}</span>
              <button type="button" role="switch" disabled={busy} aria-label={t(`notif.type.${k}`, { defaultValue: k })} aria-checked={prefs[k] !== false} onClick={() => void toggle(k)} className={`relative h-6 w-11 rounded-full transition ${prefs[k] !== false ? "bg-green" : "bg-line"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${prefs[k] !== false ? "start-[22px]" : "start-0.5"}`} />
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-ink-3">{t("notif.hint")}</p>
      </div>
    </div>
  );
}
